import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, SkipBack, SkipForward, ListMusic, Share2 } from 'lucide-react';
import SharePanel from './SharePanel';
import { ApiRelease, ApiTrack } from '@/hooks/useSiteContent';
import { mediaUrl } from '@/lib/api';
import { releaseEmbed } from '@/lib/embed';
import { audioBus } from '@/lib/audioBus';
import { fmtTime } from '@/hooks/useAudioPlayer';

export const playableTracks = (r: ApiRelease) => (r.tracks || []).filter((t) => !!t.audio_url);

const PlayIcon = () => (
  <svg viewBox="0 0 12 13" width="13" height="14" aria-hidden="true">
    <path d="M1.6 1.2c0-.7.8-1.1 1.4-.7l8.2 5.2c.5.3.5 1.1 0 1.5L3 12.4c-.6.4-1.4 0-1.4-.7V1.2z" fill="currentColor" />
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 12 13" width="12" height="13" aria-hidden="true">
    <rect x="1" y="0.5" width="3.2" height="12" rx="1.2" fill="currentColor" />
    <rect x="7.6" y="0.5" width="3.2" height="12" rx="1.2" fill="currentColor" />
  </svg>
);

/** Native tracklist player for a release (plays uploaded MP3s one after another). */
export default function ReleasePlayer({ release }: { release: ApiRelease }) {
  const tracks = useMemo(() => release.tracks || [], [release.tracks]);
  const emb = releaseEmbed(release);
  const firstPlayable = tracks.findIndex((t) => !!t.audio_url);
  const [tab, setTab] = useState<'tracks' | 'embed'>(firstPlayable >= 0 || !emb ? 'tracks' : 'embed');
  const [sharing, setSharing] = useState(false);
  const [idx, setIdx] = useState(Math.max(0, firstPlayable));
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(tracks[Math.max(0, firstPlayable)]?.duration || 0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const idxRef = useRef(idx);
  idxRef.current = idx;

  const nextPlayable = useCallback(
    (from: number, dir: 1 | -1) => {
      for (let i = from + dir; i >= 0 && i < tracks.length; i += dir) if (tracks[i].audio_url) return i;
      return -1;
    },
    [tracks],
  );

  const playAt = useCallback(
    (i: number) => {
      const a = audioRef.current;
      const t = tracks[i];
      if (!a || !t?.audio_url) return;
      const url = mediaUrl(t.audio_url);
      if (a.getAttribute('src') !== url) {
        a.src = url;
        setPos(0);
        setDur(t.duration || 0);
      }
      setIdx(i);
      setLoading(true);
      audioBus.claim('release');
      a.play().catch(() => {
        setLoading(false);
        setPlaying(false);
      });
    },
    [tracks],
  );

  // keep the 'ended' handler pointed at the latest closures
  const nextPlayableRef = useRef(nextPlayable);
  nextPlayableRef.current = nextPlayable;
  const playAtRef = useRef(playAt);
  playAtRef.current = playAt;

  useEffect(() => {
    const a = new Audio();
    a.preload = 'metadata';
    audioRef.current = a;
    const onTime = () => setPos(a.currentTime);
    const onMeta = () => isFinite(a.duration) && a.duration > 0 && setDur(a.duration);
    const onPlaying = () => {
      setPlaying(true);
      setLoading(false);
    };
    const onPause = () => {
      setPlaying(false);
      setLoading(false);
    };
    const onWaiting = () => setLoading(true);
    const onEnded = () => {
      const n = nextPlayableRef.current(idxRef.current, 1);
      if (n >= 0) playAtRef.current(n);
      else {
        setPlaying(false);
        setPos(0);
      }
    };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('playing', onPlaying);
    a.addEventListener('pause', onPause);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onPause);
    const off = audioBus.on((o) => o !== 'release' && a.pause());
    return () => {
      off();
      a.pause();
      a.removeAttribute('src');
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('playing', onPlaying);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onPause);
    };
  }, []);

  // leaving the tracklist tab (e.g. to the Spotify player) pauses our audio
  useEffect(() => {
    if (tab === 'embed') audioRef.current?.pause();
  }, [tab]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing || loading) a.pause();
    else playAt(idx);
  };
  const clickTrack = (i: number) => {
    if (!tracks[i]?.audio_url) return;
    if (i === idx && (playing || loading)) audioRef.current?.pause();
    else playAt(i);
  };
  const seekTo = (v: number) => {
    const a = audioRef.current;
    if (!a || !a.getAttribute('src')) return;
    a.currentTime = v;
    setPos(v);
  };

  const cur: ApiTrack | undefined = tracks[idx];
  const total = useMemo(() => tracks.reduce((s, t) => s + (t.duration || 0), 0), [tracks]);
  const prev = nextPlayable(idx, -1);
  const next = nextPlayable(idx, 1);
  const meta = [release.type, release.year].filter(Boolean).join(' • ').toUpperCase();

  return (
    <div className="ek-rp" data-testid="release-player">
      <div className="ek-rp-head">
        {release.cover_url ? <img className="ek-rp-cover" src={mediaUrl(release.cover_url)} alt={`${release.title} cover art`} /> : <span className="ek-rp-cover" />}
        <div className="ek-rp-titles">
          {meta && <p className="ek-modal-kicker">{meta}</p>}
          <h3 className="ek-modal-title" data-testid="release-player-title">{release.title}</h3>
          <p className="ek-modal-sub">
            {release.artist}
            {tracks.length > 0 && ` · ${tracks.length} track${tracks.length === 1 ? '' : 's'}${total ? ` · ${fmtTime(total)}` : ''}`}
          </p>
          {release.slug && (
            <button type="button" className={`ek-rp-share ${sharing ? 'is-on' : ''}`} onClick={() => setSharing((v) => !v)} aria-expanded={sharing} data-testid="release-player-share-button">
              <Share2 size={13} /> Share
            </button>
          )}
        </div>
      </div>
      {sharing && release.slug && <SharePanel release={release} onClose={() => setSharing(false)} />}
      {release.description && <p className="ek-rp-desc" data-testid="release-player-description">{release.description}</p>}

      {emb && tracks.length > 0 && (
        <div className="ek-rp-tabs" role="tablist" aria-label="Player">
          <button type="button" role="tab" aria-selected={tab === 'tracks'} className={tab === 'tracks' ? 'is-on' : ''} onClick={() => setTab('tracks')} data-testid="release-player-tab-tracks">
            <ListMusic size={13} /> Tracklist
          </button>
          <button type="button" role="tab" aria-selected={tab === 'embed'} className={tab === 'embed' ? 'is-on' : ''} onClick={() => setTab('embed')} data-testid="release-player-tab-embed">
            {emb.provider === 'spotify' ? 'Spotify' : 'Player'}
          </button>
        </div>
      )}

      {tab === 'tracks' && !tracks.length && !emb ? (
        <p className="ek-rp-empty" data-testid="release-player-empty">
          {release.spotify_url ? 'Stream it on Spotify below.' : 'Playback for this release is coming soon.'}
        </p>
      ) : tab === 'tracks' ? (
        <>
          <div className="ek-rp-now" data-testid="release-player-now">
            <button type="button" className="ek-rp-skip" onClick={() => prev >= 0 && playAt(prev)} disabled={prev < 0} aria-label="Previous track" data-testid="release-player-prev-button">
              <SkipBack size={15} />
            </button>
            <button
              type="button"
              className={`ek-rp-play ${loading ? 'is-loading' : ''}`}
              onClick={toggle}
              disabled={firstPlayable < 0}
              aria-label={playing || loading ? 'Pause' : 'Play'}
              data-state={playing || loading ? 'pause' : 'play'}
              data-testid="release-player-play-button"
            >
              {playing || loading ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button type="button" className="ek-rp-skip" onClick={() => next >= 0 && playAt(next)} disabled={next < 0} aria-label="Next track" data-testid="release-player-next-button">
              <SkipForward size={15} />
            </button>
            <div className="ek-rp-progress">
              <div className="ek-rp-now-title" data-testid="release-player-current-title">{cur?.title || '—'}</div>
              <input
                type="range"
                min={0}
                max={dur || 0}
                step={0.1}
                value={Math.min(pos, dur || 0)}
                onChange={(e) => seekTo(Number(e.target.value))}
                aria-label="Seek"
                className="ek-rp-range"
                style={{ ['--p' as string]: `${dur ? (Math.min(pos, dur) / dur) * 100 : 0}%` } as React.CSSProperties}
                data-testid="release-player-seek"
              />
              <div className="ek-rp-times">
                <span data-testid="release-player-position">{fmtTime(pos)}</span>
                <span>{fmtTime(dur)}</span>
              </div>
            </div>
          </div>
          {tracks.length ? (
            <ol className="ek-rp-list" data-testid="release-player-tracklist">
              {tracks.map((t, i) => {
                const on = i === idx && (playing || loading);
                const ok = !!t.audio_url;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`ek-rp-row ${i === idx ? 'is-current' : ''} ${on ? 'is-playing' : ''}`}
                      onClick={() => clickTrack(i)}
                      disabled={!ok}
                      aria-current={i === idx ? 'true' : undefined}
                      data-testid={`release-player-track-${i}`}
                    >
                      <span className="ek-rp-num">
                        {on ? (
                          <span className="ek-rp-eq" aria-label="Playing">
                            <i />
                            <i />
                            <i />
                          </span>
                        ) : (
                          i + 1
                        )}
                      </span>
                      <span className="ek-rp-name">
                        <span>{t.title}</span>
                        <small>{ok ? t.artist : 'Coming soon'}</small>
                      </span>
                      <span className="ek-rp-dur">{t.duration ? fmtTime(t.duration) : ''}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          ) : null}
        </>
      ) : (
        emb && (
          <iframe
            className="ek-modal-iframe"
            data-testid="embed-player-iframe"
            title={`${release.title} — embedded player`}
            src={emb.src}
            height={emb.height}
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          />
        )
      )}
      {release.spotify_url && (
        <a className="ek-modal-open" href={release.spotify_url} target="_blank" rel="noopener noreferrer" data-testid="embed-player-open-external">
          Open in Spotify <ExternalLink size={13} />
        </a>
      )}
    </div>
  );
}
